import { z } from 'zod';

/**
 * One entry of the CONTACT_TAG_DEFINITIONS setting (stored as a JSON array in
 * the settings table). The TYPE owns the color: a type groups many tags that
 * all share its color, and a contact holds at most one tag per type — adding
 * another tag of the type replaces the current one. Tags stay plain strings
 * on contacts.tags.
 */
export const tagGroupSchema = z.object({
  /** Type name (e.g. "source"). Empty = colored tags without exclusivity. */
  type: z.string(),
  /** Hex color like #22c55e, shared by every tag of the type. */
  color: z.string().optional(),
  tags: z.array(z.string()),
});

export type TagGroup = z.infer<typeof tagGroupSchema>;

/** Legacy per-tag entry ({name, type, color}) from the first catalog format. */
const legacyTagEntrySchema = z.object({
  name: z.string(),
  type: z.string().nullable().optional(),
  color: z.string().optional(),
});

/** Flat per-tag view of the catalog — what render/enforcement code consumes. */
export interface TagDefinition {
  name: string;
  type?: string | null;
  color?: string;
}

/**
 * Parse the CONTACT_TAG_DEFINITIONS setting into type groups. Lenient: invalid
 * entries are skipped individually, empty draft groups survive (the settings
 * editor depends on this), and legacy per-tag entries are folded into groups.
 */
export const parseTagGroups = (raw: string | null | undefined): TagGroup[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const groups: TagGroup[] = [];
    for (const item of parsed) {
      const group = tagGroupSchema.safeParse(item);
      if (group.success) {
        groups.push(group.data);
        continue;
      }
      const legacy = legacyTagEntrySchema.safeParse(item);
      if (!legacy.success || !legacy.data.name.trim()) continue;
      const type = legacy.data.type?.trim() ?? '';
      let target = groups.find((g) => g.type.trim() === type);
      if (!target) {
        target = { type, color: legacy.data.color, tags: [] };
        groups.push(target);
      }
      if (!target.color && legacy.data.color) target.color = legacy.data.color;
      if (!target.tags.includes(legacy.data.name.trim())) target.tags.push(legacy.data.name.trim());
    }
    return groups;
  } catch {
    return [];
  }
};

/** Flatten groups to per-tag definitions (blank names dropped, first name wins). */
export const flattenTagGroups = (groups: TagGroup[]): TagDefinition[] => {
  const defs: TagDefinition[] = [];
  const seen = new Set<string>();
  for (const group of groups) {
    const type = group.type.trim();
    for (const raw of group.tags) {
      const name = raw.trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      defs.push({ name, type: type || undefined, color: group.color });
    }
  }
  return defs;
};

/** Flat per-tag catalog straight from the setting value. */
export const parseTagDefinitions = (raw: string | null | undefined): TagDefinition[] =>
  flattenTagGroups(parseTagGroups(raw));

/** Kept for compatibility — flattening already drops blank names. */
export const namedTagDefinitions = (definitions: TagDefinition[]): TagDefinition[] =>
  definitions.filter((d) => d.name.trim().length > 0);

const normalizeType = (type: string | null | undefined): string | undefined => {
  const trimmed = type?.trim().toLowerCase();
  return trimmed ? trimmed : undefined;
};

/**
 * Enforce the one-tag-per-type rule on an ordered tag list: a later tag always
 * replaces an earlier tag of the same type (and a repeated name replaces its
 * earlier occurrence). Untyped tags are only deduped by name. The order of the
 * surviving tags is preserved.
 */
export const enforceTagTypes = (tags: string[], definitions: TagDefinition[]): string[] => {
  const typeByName = new Map<string, string>();
  for (const def of definitions) {
    const type = normalizeType(def.type);
    if (type) typeByName.set(def.name, type);
  }

  // Kept slots with tombstones so replacements don't disturb the order.
  const kept: (string | null)[] = [];
  const slotByName = new Map<string, number>();
  const slotByType = new Map<string, number>();

  for (const raw of tags) {
    const tag = raw.trim();
    if (!tag) continue;

    const prevSameName = slotByName.get(tag);
    if (prevSameName !== undefined) kept[prevSameName] = null;

    const type = typeByName.get(tag);
    if (type !== undefined) {
      const prevSameType = slotByType.get(type);
      if (prevSameType !== undefined) {
        const replaced = kept[prevSameType];
        if (replaced) slotByName.delete(replaced);
        kept[prevSameType] = null;
      }
    }

    slotByName.set(tag, kept.length);
    if (type !== undefined) slotByType.set(type, kept.length);
    kept.push(tag);
  }

  return kept.filter((t): t is string => t !== null);
};

/** Add tags onto an existing set — the incoming tags win per type and name. */
export const mergeTagsWithTypes = (
  existing: string[],
  incoming: string[],
  definitions: TagDefinition[],
): string[] => enforceTagTypes([...existing, ...incoming], definitions);
